import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import ThemeSwitch from '../components/ThemeSwitch';
import { handleApiErrorWithMessages } from '../../utils/apiErrorHandler';

const EmailChangeConfirmNewPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUserData } = useAuthStore();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setGeneralError('Invalid confirmation link');
      setIsLoading(false);
      return;
    }

    const confirmNewEmail = async () => {
      try {
        setIsLoading(true);
        setGeneralError(null);
        
        // Make GET request to confirm new email
        await api.get(`/api/auth/change-email/confirm-new?token=${encodeURIComponent(token)}`);
        
        // Refresh user data if logged in
        try {
          await refreshUserData();
        } catch (e) {
          // User might not be logged in, that's okay
        }
        
        setIsSuccess(true);
        
        // Auto-redirect after 3 seconds
        setTimeout(() => {
          navigate('/my');
        }, 3000);
      } catch (error) {
        const { generalError: apiGeneralError } = handleApiErrorWithMessages(error);
        setGeneralError(apiGeneralError || 'Failed to confirm email change');
      } finally {
        setIsLoading(false);
      }
    };

    confirmNewEmail();
  }, [token, navigate, refreshUserData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#121212] py-12 px-4 sm:px-6 relative">
      {/* Theme Switch - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <ThemeSwitch />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">
            {isLoading ? 'Confirming...' : isSuccess ? 'Email address changed successfully!' : 'Error'}
          </h1>
          <p className="mt-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
            {isLoading ? (
              'Please wait...'
            ) : isSuccess ? (
              <>
                Your email address has been changed successfully. Redirecting to dashboard...
                <br />
                <Link
                  to="/v2"
                  className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline mt-4 inline-block"
                >
                  Go to dashboard
                </Link>
              </>
            ) : (
              <>
                {generalError || 'An error occurred'}
                <br />
                <Link
                  to="/v2"
                  className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline mt-4 inline-block"
                >
                  Go to dashboard
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#121212] dark:border-[#D0D0D0]"></div>
          </div>
        )}

        {/* Error Display */}
        {generalError && !isLoading && (
          <div className="rounded-xl border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailChangeConfirmNewPage;
